import { Injectable, inject } from '@angular/core';
import { LeaderboardRange } from '@constants';
import { environment } from '@environments/environment';
import { KeplerSlapfiveService } from '@services';
import { LeaderboardCache, LeaderboardUser } from '@types';
import dayjs from 'dayjs';
import { catchError, map, Observable, of, tap } from 'rxjs';

/**
 * Global singleton service for the Leaderboard component.
 * Should not be directly used elsewhere than this feature package, but being
 * global helps DI within the child components.
 *
 * The primary purpose of this service is to expose an in-memory todo list
 * of activities from Wordpress. These are then hidden upon completion.
 * Completion is defined as posting the activity to Slapfive and adding
 * the Wordpress data to a database for long-term tracking.
 * Error handling re-adds the activity to the todo list.
 */
@Injectable({ providedIn: 'root' })
export class LeaderboardService {
    private slapfiveService = inject(KeplerSlapfiveService);

    private cache: LeaderboardCache = {
        realtime: null,
        weekly: null,
        monthly: null,
        all_time: null,
        my_stats: null,
    };

    private readonly CACHE_TTL = 1000 * 60 * 15;
    private readonly DEFAULT_PAGE_SIZE = 10;
    static readonly DEFAULT_RANGE = LeaderboardRange.REALTIME;
    private readonly otherRanges = [LeaderboardRange.ALL_TIME];

    constructor() {
        this.otherRanges.forEach((range) => {
            this.getLeaderboard(range, this.DEFAULT_PAGE_SIZE).subscribe((users) => {
                this.setCachedValue(range, users);
            });
        });
    }

    getLeaderboard(
        range: LeaderboardRange,
        pageSize = this.DEFAULT_PAGE_SIZE,
        searchQuery?: string,
    ): Observable<LeaderboardUser[]> {
        const searchValue = searchQuery?.trim();
        // Skip cache for search queries
        if (searchValue && searchValue.length > 0) {
            console.debug(`leaderboard::searching ${range} data with query: ${searchQuery}`);
            return this.slapfiveService
                .getLeaderboard({
                    range: range,
                    debug: environment.api.kepler.slapfive.leaderboard.debug,
                    page_size: pageSize,
                    query: searchValue,
                })
                .pipe(
                    map((users) => {
                        return (users ?? []).map((user) => ({
                            ...user,
                            type: range == LeaderboardRange.REALTIME ? 'realtime' : 'periodic',
                        }));
                    }),
                    catchError((err) => {
                        console.error('An error occurred while searching the leaderboard', err);
                        return of([]);
                    }),
                );
        }

        // Use cache for non-search queries
        const leaderboard = this.getCachedValue(range);
        if (leaderboard) {
            console.debug(`leaderboard::retrieved cached ${range} data`, leaderboard);
            return of(leaderboard);
        } else {
            // we can get here if the component finishes rendering before the cached values are set in this service's constructor
            console.debug(`leaderboard::fetching ${range} data`, pageSize);
            return this.slapfiveService
                .getLeaderboard({
                    range: range,
                    debug: environment.api.kepler.slapfive.leaderboard.debug,
                    page_size: pageSize,
                })
                .pipe(
                    map((users) => {
                        return (users ?? []).map((user) => ({
                            ...user,
                            type: range == LeaderboardRange.REALTIME ? 'realtime' : 'periodic',
                        }));
                    }),
                    catchError((err) => {
                        console.error(
                            'An error occurred while fetching the leaderboard, falling back to expired cache',
                            err,
                        );
                        if (this.cache[range]) return of(this.cache[range].data);
                        else {
                            console.error('No cached leaderboard found', err);
                            return of([]); // proceed onward
                        }
                    }),
                    tap((users) => {
                        this.setCachedValue(range, users);
                        console.debug(`leaderboard::${range} cache updated with fetch response`, users);
                    }),
                );
        }
    }

    get totalUserCount$(): Observable<number> {
        return this.slapfiveService.totalUserCount$.pipe(map((count) => count || 48380)); // fallback is manually found in Slapfive dashboard
    }

    private getCachedValue(range: LeaderboardRange): LeaderboardUser[] | null {
        if (this.isCacheExpired(range)) return null;
        else return this.cache[range].data;
    }

    private setCachedValue(range: LeaderboardRange, users: LeaderboardUser[]): void {
        if (!users) return;
        this.cache[range] = {
            data: users,
            timestamp: dayjs().valueOf(),
        };
    }

    private isCacheExpired(range: LeaderboardRange): boolean {
        const cache = this.cache[range];
        if (!cache) return true;
        const cacheAge = dayjs().valueOf() - cache.timestamp;
        return cacheAge > this.CACHE_TTL;
    }
}
